import type {Configuracion} from "services-comun-tiempo/modules/portal/config/network";
import type {IRespuestaV4DataRedaccionNewsWeb as INoticia} from "services-comun-meteored/modules/services/interface";
import type {Noticias} from "i18n/noticias";
import type {Calendario} from "i18n/calendario";

export interface Traduccion {
    calendario: Calendario;
    noticias: Noticias;
}

export class Noticia {
    /* STATIC */
    private static readonly FOTO_768: string = `_768.`;
    private static readonly FOTO_512: string = `_512.`;
    private static readonly FOTO_320: string = `_320.`;

    /* INSTANCE */
    public readonly id: number;
    public readonly titulo: string;
    public readonly url: string;
    public readonly categoria: number;
    public readonly subcategoria?: number;
    public readonly promocionado: boolean;
    public readonly urlFoto: string;
    public readonly video: number;
    public readonly publicacionFecha: string;
    public readonly autor: {
        nombre: string;
        foto: string;
    };
    public readonly pixel: string;

    private readonly baseFotos: string;
    private readonly baseFotosMini: string;

    public constructor(protected readonly dominio: Configuracion, data: INoticia, protected readonly traduccion: Traduccion) {
        this.id = data.id;
        this.titulo = data.titulo;
        this.url = data.url;
        this.categoria = data.categoria;
        this.subcategoria = data.subcategoria;
        this.promocionado = data.promocionado ?? false;
        this.urlFoto = data.foto;
        this.video = data.video;
        this.publicacionFecha = data.fecha_publicacion;
        this.autor = data.autor ?? { nombre: "", foto: "" }; // todo checkear el autor
        this.pixel = `https://${dominio.www}.${dominio.base}/img/apixel.png`;

        this.baseFotos = `https://${dominio.servicesMR}/img/article`; // fijo a producción
        this.baseFotosMini = `https://${dominio.servicesMR}/img/article/mini`; // fijo a producción
    }

    public get seccion(): string {
        return this.traduccion.noticias.categorias(`${this.categoria}`);
    }

    public get subseccion(): string {
        if (this.subcategoria==undefined || this.subcategoria==0) {
            return this.seccion;
        }

        return this.traduccion.noticias.categorias_ram(`${this.subcategoria}`);
    }

    public get publicacion(): string {
        const fecha = new Date();
        const publicacion = new Date(this.publicacionFecha);

        const minutos = Math.floor((fecha.getTime() - publicacion.getTime())/60000);

        if (minutos < 60) { // publicada hace menos de 1 hora
            return this.traduccion.calendario.hace_x_minutos({minutos: `${minutos}`});
        }
        if (minutos < 120) { // publicada hace menos de 2 horas
            return this.traduccion.calendario.hace_1_hora;
        }
        if (minutos <= 600) { // publicada hace menos de 10 horas
            return this.traduccion.calendario.hace_x_horas({horas: `${Math.floor(minutos/60)}`});

        }
        if (fecha.toDateString() == publicacion.toDateString()) { // publicada hoy
            return this.traduccion.calendario.hoy;
        }
        if (fecha.getDate() - 1 == publicacion.getDate()) { // publicada ayer
            return this.traduccion.calendario.ayer;
        }
        if(fecha.getFullYear() == publicacion.getFullYear()) { // publicada este año
            return `${publicacion.getDate()} ${this.traduccion.calendario.meses_corto(`${publicacion.getMonth()+1}`)}`;
        }

        return `${publicacion.getDate()} ${this.traduccion.calendario.meses_corto(`${publicacion.getMonth()+1}`)} ${publicacion.getFullYear()}`;

    }

    private checkFoto(): string {
        if (this.urlFoto.includes(Noticia.FOTO_768)) {
            return this.urlFoto.replace(Noticia.FOTO_768, ".");
        }

        if (this.urlFoto.includes(Noticia.FOTO_512)) {
            return this.urlFoto.replace(Noticia.FOTO_512, ".");
        }

        if (this.urlFoto.includes(Noticia.FOTO_320)) {
            return this.urlFoto.replace(Noticia.FOTO_320, ".");
        }

        return this.urlFoto;
    }

    private checkFoto320(): string {
        if (this.urlFoto.includes(Noticia.FOTO_768)) {
            return this.urlFoto.replace(Noticia.FOTO_768, Noticia.FOTO_320);
        }

        if (this.urlFoto.includes(Noticia.FOTO_512)) {
            return this.urlFoto.replace(Noticia.FOTO_512, Noticia.FOTO_512);
        }

        if (this.urlFoto.includes(Noticia.FOTO_320)) {
            return this.urlFoto;
        }

        return this.urlFoto.replace(".", Noticia.FOTO_320);
    }

    private checkFoto512(): string {
        if (this.urlFoto.includes(Noticia.FOTO_768)) {
            return this.urlFoto.replace(Noticia.FOTO_768, Noticia.FOTO_512);
        }

        if (this.urlFoto.includes(Noticia.FOTO_320)) {
            return this.urlFoto.replace(Noticia.FOTO_320, Noticia.FOTO_512);
        }

        if (this.urlFoto.includes(Noticia.FOTO_512)) {
            return this.urlFoto;
        }

        return this.urlFoto.replace(".", Noticia.FOTO_512);
    }

    private checkFoto768(): string {
        if (this.urlFoto.includes(Noticia.FOTO_512)) {
            return this.urlFoto.replace(Noticia.FOTO_512, Noticia.FOTO_768);
        }

        if (this.urlFoto.includes(Noticia.FOTO_320)) {
            return this.urlFoto.replace(Noticia.FOTO_320, Noticia.FOTO_768);
        }

        if (this.urlFoto.includes(Noticia.FOTO_768)) {
            return this.urlFoto;
        }

        return this.urlFoto.replace(".", Noticia.FOTO_768);
    }

    public get foto(): string {
        return `${this.baseFotos}/${this.checkFoto()}`;
    }

    public get fotoMini(): string {
        return `${this.baseFotosMini}/${this.checkFoto()}`;
    }

    public get foto320(): string {
        return `${this.baseFotos}/${this.checkFoto320()}`;
    }

    public get foto512(): string {
        return `${this.baseFotos}/${this.checkFoto512()}`;
    }

    public get foto768(): string {
        return `${this.baseFotos}/${this.checkFoto768()}`;
    }
}
